import React, { FC, useMemo } from 'react'

import { ThemeProps } from '@hhgtech/hhg-components'
import { Container } from 'components/atoms/container'
import {
  SectionContainerStyled,
  StyledContainer,
} from 'components/atoms/container/index.styled'
import { AdBlock } from 'components/molecules/adBlock'
import { FixedBottomAdBlock } from 'components/molecules/adBlock/fixedBottomAdBlock'
import { domainLocales } from 'domainLocales'
import {
  ICategoryProps,
  ICommunity,
  IDescriptionCategory,
  IFAQ,
  IQA,
} from 'interfaces/categories.interface'
import { Article } from 'interfaces/types'
import { useRouter } from 'next/router'
import styled from 'styled-components'
import { deviceLayout } from 'styles/breakpoint'
import { getAdTag, getCountryAdTag, getLocaleCategoryAdTag } from 'utils/ads'

import Breadcrumb from '../Breadcrumb'
import withCategory from '../withCategory'
import AsideCategories from './AsideCategories'
import Communities from './Communities'
import Description, { DescriptionPC } from './Description'
import Discovers from './Discovers'
import GeneralKnowledge from './GeneralKnowledge'
import QAs from './QAs'

const WrapperLayout = styled.div`
  .category-container {
    ${deviceLayout.pcLayout`
      .breadcrumb, .des, .general-knowledge{
        padding-left: 16px;
        padding-right: 16px;
      }
      // .container-des::before{
      //   margin-left: -16px;
      // }
    `}
    ${deviceLayout.spLayout`
        border-radius: 16px 16px 0px 0px;
    `}
    background: ${(props: ThemeProps & { 'data-bg'?: string }) =>
      props['data-bg'] || props.theme.colors.white};

    .des-header {
    }
  }
  .root-container {
    padding-bottom: 0;
    background-color: transparent;
    font-size: 14px;
    line-height: 24px;
  }
  .footer-container {
    /* ${deviceLayout.pcLayout`
        background: ${(props: ThemeProps & { 'data-bg'?: string }) =>
          props['data-bg'] || props.theme.colors.white};
    `} */
    /* background: ${(props) => props.theme.mbColors.lightBlue}; */
  }

  .communities-container,
  .articles-container,
  .qa-container {
    padding: 24px 0;
  }

  .category-container {
    header,
    h1 {
      font-size: 24px;
      font-weight: 600;
    }

    .des,
    .main {
      padding-top: 12px;
      & > * {
        padding-top: 12px;
        padding-bottom: 12px;
      }
    }
  }

  .general {
    ${deviceLayout.pcLayout`
        padding-top: 70px;
    `}
    ${deviceLayout.spLayout`
      border-radius: 16px 16px 0px 0px;
      margin-bottom: -20px;
        & > * {
            padding: 16px;
        }
    `}
    padding: 0;
    padding-top: 12px;
    background: ${(props: ThemeProps) => props.theme.colors.white};
    header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      font-size: 18px;
    }
  }

  .main {
    display: flex;
    .aside-content {
      width: 30%;
      margin: 12px;
      min-width: 300px;
      .ad-banner {
        padding-bottom: 24px;
      }
      .aside-category {
        width: 100%;
        padding-right: 0;
        padding-left: 0;
        margin-top: 0;
        margin-bottom: 12px;
      }
    }
    .main-content {
      width: 70%;
      margin: 12px;
    }
  }
`

const Category: FC<ICategoryProps> = (props: ICategoryProps) => {
  const {
    subCategories,
    categoryInfo,
    isMobile,
    explainersPosts,
    categoryParent,
    relatedArticle,
    community,
  } = props

  const descriptionCategory: IDescriptionCategory = {
    imageColor: categoryInfo?.thumnailColor,
    subCategories: subCategories,
    header: categoryInfo.categoryName || categoryInfo.displayName,
    banner: categoryInfo.featureImage,
    description: categoryInfo.description,
    author: categoryInfo?.reviewedBy?.displayName
      ? {
          avatar:
            categoryInfo?.reviewedBy?.avatarThumbnail ||
            categoryInfo?.reviewedBy?.profileUrl ||
            '',
          name: categoryInfo?.reviewedBy?.displayName || '',
          createdAt: categoryInfo?.reviewedBy?.reviewedDate || '',
        }
      : null,
    sponsored: categoryInfo.sponsor,
  }

  const qas: IQA[] =
    categoryInfo.faqs?.map((faq: IFAQ) => ({
      isMobile,
      title: faq.title,
      contentString: faq.content,
    })) || []

  const { locale = 'vi-VN' } = useRouter()
  const sL = locale || 'vi-VN' // standardized locale
  const [secondBreadcrumb, ..._breadcrumbItems] = categoryParent
  const breadcrumbs = {
    secondItem: {
      label: secondBreadcrumb?.displayName || '',
      value: secondBreadcrumb?.permalink || '',
      dataEventCategory: 'Breadcrumb',
      dataEventAction: 'Click',
      dataEventLabel: `https://${domainLocales[sL]}${secondBreadcrumb?.permalink}`,
    },
    breadcrumbItems:
      _breadcrumbItems?.map((c) => ({
        label: c.displayName || '',
        value: c.permalink || '',
        dataEventCategory: 'Breadcrumb',
        dataEventAction: 'Click',
        dataEventLabel: `https://${domainLocales[sL]}${c.permalink}`,
      })) || [],
  }

  const idExplainerPosts: string = explainersPosts
    ?.map((post: Article) => post.id)
    .toString()

  const communities = Array.isArray(community)
    ? (community as ICommunity[])
    : [community].filter((item: ICommunity) => !!item)

  const adUnitPath = getAdTag({
    countryAdTag: getCountryAdTag(sL, {
      isMobile,
      // isAmp,
    }),
    contentAdTag: getLocaleCategoryAdTag(sL, categoryInfo.permalink),
  })

  const contentId = useMemo(() => {
    if (typeof window !== 'undefined') {
      const contentIdContainer = document.querySelector<HTMLMetaElement>(
        'meta[name="content_id"]',
      )
      return contentIdContainer?.content
    }
    return undefined
  }, [categoryInfo])

  return (
    <SectionContainerStyled className="category-container">
      <Container className="breadcrumb">
        <Breadcrumb {...breadcrumbs} />
      </Container>
      {isMobile ? (
        <Description {...descriptionCategory} />
      ) : (
        <DescriptionPC {...descriptionCategory} />
      )}
      {isMobile ? (
        <SectionContainerStyled as="article" className="general">
          <GeneralKnowledge isMobile={isMobile} articles={explainersPosts} />
          {!!communities.length && (
            <Communities communities={community || []} />
          )}
          <Discovers
            isMobile={isMobile}
            articles={relatedArticle}
            categoryInfo={categoryInfo}
            idExplainerPosts={idExplainerPosts}
          />
          <AdBlock
            className="ad-banner"
            divId={`div-gpt-ad-midbanner-mobile1`}
            adUnitPath={adUnitPath}
            size={[300, 250]}
            pos={['MidBanner1']}
            contentId={contentId}
          />
          {/* <AdBlock
            className="ad-banner"
            divId={`div-gpt-ad-endbanner-mobile1`}
            adUnitPath={adUnitPath}
            size={[300, 600]}
            pos={['EndBanner']}
            contentId={contentId}
          /> */}
          {!!qas.length && <QAs qas={qas} />}
        </SectionContainerStyled>
      ) : (
        <SectionContainerStyled as="article" className="general">
          <GeneralKnowledge isMobile={isMobile} articles={explainersPosts} />
          <StyledContainer className="main">
            <SectionContainerStyled as="main" className="main-content">
              <Discovers
                isMobile={isMobile}
                articles={relatedArticle}
                categoryInfo={categoryInfo}
                idExplainerPosts={idExplainerPosts}
              />
              {!!qas.length && <QAs qas={qas} />}
            </SectionContainerStyled>
            <SectionContainerStyled as="aside" className="aside-content">
              {!!communities.length && (
                <Communities communities={community || []} />
              )}
              <AdBlock
                className="ad-banner"
                divId={`div-gpt-ad-midbanner1`}
                adUnitPath={adUnitPath}
                size={[300, 250]}
                pos={['MidBanner1']}
                contentId={contentId}
              />
              <AsideCategories categories={subCategories} />
              {/* <AdBlock
                className="ad-banner"
                divId={`div-gpt-ad-endbanner1`}
                adUnitPath={adUnitPath}
                size={[300, 600]}
                pos={['EndBanner']}
                contentId={contentId}
              /> */}
            </SectionContainerStyled>
          </StyledContainer>
        </SectionContainerStyled>
      )}
      <FixedBottomAdBlock
        divId={'div-gpt-ad-category-sticky-' + categoryInfo.termId}
        adUnitPath={adUnitPath}
        isMobile={isMobile}
      />
    </SectionContainerStyled>
  )
}

export default withCategory<ICategoryProps>(Category, WrapperLayout)
